import { Create, useForm, useSelect } from "@refinedev/antd";
import { HttpError, useOne } from "@refinedev/core";
import {
  Form,
  Select,
  Radio,
  DatePicker,
  Switch,
  Row,
  Col,
  Card,
  App,
  Breadcrumb,
  Input,
} from "antd";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router";
import type { IncidenceCreateFormValues } from "../../types";
import { HomeOutlined, AuditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { usePageTitle } from "../../hooks";

const { TextArea } = Input;

/**
 * IncidenceCreate Component
 * Form for creating a new incidence from an audit detail page
 * Requires auditId in query params
 */
export const IncidenceCreate = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auditId = searchParams.get("auditId");
  
  usePageTitle("Nueva Incidencia");

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load audit data for breadcrumb
  const { query } = useOne({
    resource: "audits",
    id: auditId || "",
    queryOptions: {
      enabled: !!auditId,
    },
  });

  const audit = query.data?.data;

  const { formProps, form, onFinish } = useForm<
    IncidenceCreateFormValues,
    HttpError,
    IncidenceCreateFormValues
  >({
    action: "create",
    // Repointed to the unified Ticket endpoint. The serializer
    // accepts the audit-specific payload (audit, priority, deadline,
    // …) as part of the same POST.
    resource: "v1/tickets",
    redirect: false, // Disable automatic redirect
    onMutationSuccess: (data) => {
      message.success(
        t("incidence.create_success", "Incidencia creada exitosamente")
      );
      // Redirect to the detail page of the created incidence
      const createdId = (data.data as any)?.id;
      if (createdId) {
        navigate(`/incidences/${createdId}`);
      }
    },
    onMutationError: (error) => {
      message.error(
        error?.message ||
          t("incidence.create_error", "Error al crear la incidencia")
      );
    },
  });

  // Load audit-incidence types from the unified TicketCategory
  // catalogue (kind=audit). The option value is the TicketCategory
  // pk, which is what the Ticket payload expects under `category`.
  const { selectProps: kindIncidenceSelectProps } = useSelect({
    resource: "v1/ticket-categories",
    optionLabel: "name",
    optionValue: "id",
    pagination: { pageSize: 200 },
    filters: [
      { field: "kind", operator: "eq", value: "audit" },
    ],
  });

  // Load users for assigned_to select dropdown
  const { selectProps: userSelectProps } = useSelect({
    resource: "app-users",
    optionLabel: "name",
    optionValue: "id",
    pagination: {
      pageSize: 100,
    },
  });

  // Set default values with audit pre-filled
  useEffect(() => {
    if (auditId) {
      form.setFieldsValue({
        audit: Number(auditId),
        priority: "medium",
        requires_immediate_action: false,
      });
    }
  }, [form, auditId]);

  /**
   * Handle form submission
   */
  const handleSubmit = async (values: IncidenceCreateFormValues) => {
    try {
      setIsSubmitting(true);

      // Format deadline if provided
      const data: any = {
        ...values,
        deadline: values.deadline
          ? dayjs(values.deadline).format("YYYY-MM-DD")
          : null,
      };

      await onFinish(data);
    } catch (error: any) {
      // Error handled by mutation
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Create
      title={t("incidence.create", "Crear Incidencia")}
      breadcrumb={
        <Breadcrumb
          items={[
            {
              title: <HomeOutlined />,
              href: `/audits/${auditId}`,
              onClick: (e: any) => {
                e.preventDefault();
                navigate("/");
              },
            },
            {
              title: (
                <>
                  <AuditOutlined /> {t("audit.title", "Auditorías")}
                </>
              ),
              href: "/audits",
              onClick: (e: any) => {
                e.preventDefault();
                navigate("/audits");
              },
            },
            {
              title: (audit as any)?.reference || `Auditoría #${auditId}`,
              href: `/audits/${auditId}`,
              onClick: (e: any) => {
                e.preventDefault();
                navigate(`/audits/${auditId}`);
              },
            },
            {
              title: t("incidence.create", "Crear Incidencia"),
            },
          ]}
        />
      }
      saveButtonProps={{
        loading: isSubmitting,
        onClick: () => {
          form.submit();
        },
      }}
    >
      <Form {...formProps} layout="vertical" onFinish={handleSubmit}>
        {/* Hidden field for audit ID */}
        <Form.Item name="audit" hidden>
          <Input type="hidden" />
        </Form.Item>

        <Card
          title={t("incidence.basicInfo", "Información Básica")}
          style={{ marginBottom: 24 }}
        >
          <Row gutter={16}>
            <Col xs={24} sm={24} md={12}>
              <Form.Item
                label={t("incidence.type", "Tipo de Incidencia")}
                name="kind_incidence"
                rules={[
                  {
                    required: true,
                    message: t(
                      "incidence.type_required",
                      "El tipo de incidencia es requerido"
                    ),
                  },
                ]}
              >
                <Select
                  {...kindIncidenceSelectProps}
                  placeholder={t(
                    "incidence.type_placeholder",
                    "Seleccione un tipo"
                  )}
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item
                label={t("incidence.description", "Descripción")}
                name="description"
                rules={[
                  {
                    required: true,
                    message: t(
                      "incidence.description_required",
                      "La descripción es requerida"
                    ),
                  },
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder={t(
                    "incidence.description_placeholder",
                    "Describa la incidencia detectada..."
                  )}
                  maxLength={1000}
                  showCount
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          title={t("incidence.priority_and_assignment", "Prioridad y Asignación")}
          style={{ marginBottom: 24 }}
        >
          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item
                label={t("incidence.priority_label", "Prioridad")}
                name="priority"
                rules={[
                  {
                    required: false,
                  },
                ]}
              >
                <Radio.Group>
                  <Radio value="low">
                    {t("incidence.priority.low", "Baja")}
                  </Radio>
                  <Radio value="medium">
                    {t("incidence.priority.medium", "Media")}
                  </Radio>
                  <Radio value="high">
                    {t("incidence.priority.high", "Alta")}
                  </Radio>
                  <Radio value="critical">
                    {t("incidence.priority.critical", "Crítica")}
                  </Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={24} md={12}>
              <Form.Item
                label={t("incidence.assigned_to", "Asignar a")}
                name="assigned_to"
                rules={[
                  {
                    required: false,
                  },
                ]}
              >
                <Select
                  {...userSelectProps}
                  placeholder={t(
                    "incidence.assigned_to_placeholder",
                    "Seleccione un usuario (opcional)"
                  )}
                  showSearch
                  allowClear
                  filterOption={(input, option) =>
                    String(option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={12}>
              <Form.Item
                label={t("incidence.deadline", "Fecha Límite")}
                name="deadline"
                rules={[
                  {
                    required: false,
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder={t(
                    "incidence.deadline_placeholder",
                    "Seleccione una fecha (opcional)"
                  )}
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item
                label={t(
                  "incidence.requires_immediate_action",
                  "Requiere Acción Inmediata"
                )}
                name="requires_immediate_action"
                valuePropName="checked"
                tooltip={t(
                  "incidence.requires_immediate_action_tooltip",
                  "Marque esta opción si la incidencia requiere atención urgente"
                )}
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Form>
    </Create>
  );
};
